// Family exploration on the results page. Renders nothing unless the server gate is on.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, FlaskConical } from "lucide-react";
import { usePublishedGuides } from "@/lib/subjectGuides";
import { fetchFamilyPreview, fetchFamilyPreviewFlag, saveFamilyPreview } from "@/pages/beta/courseFitApi";
import { guideHref } from "@/lib/fastFitGuideContext";

function FamilyCard({ card, chosen, disabled, onToggle }) {
  return (
    <button type="button" onClick={onToggle} disabled={disabled} aria-pressed={chosen}
      data-testid={`family-card-${card.slug}`}
      className={`flex min-w-0 flex-col items-start rounded-xl border p-4 text-left transition-colors ${chosen ? "border-[#7C3AED] bg-[#7C3AED]/5 ring-1 ring-[#7C3AED]" : disabled ? "cursor-not-allowed border-stone-200 bg-stone-50 opacity-60" : "border-slate-200 bg-white hover:border-[#7C3AED]"}`}>
      <span className="flex w-full items-start justify-between gap-2">
        <span className="break-words text-sm font-semibold text-slate-900">{card.family}</span>
        {chosen && <Check className="h-4 w-4 shrink-0 text-[#7C3AED]" aria-hidden="true" />}
      </span>
      <span className="mt-1 text-xs text-slate-500" data-testid={`family-card-count-${card.slug}`}>
        {card.memberCount} subjects · {card.scoredMemberCount} scored in this beta
      </span>
      {card.fromChoice?.length > 0 && (
        <span className="mt-2 inline-flex rounded-full bg-[#7C3AED]/10 px-2 py-0.5 text-[11px] font-medium text-[#7C3AED]" data-testid={`family-card-from-choice-${card.slug}`}>
          From your choice: {card.fromChoice.join(", ")}
        </span>
      )}
      {card.scoredHighlights.length > 0 && (
        <span className="mt-2 text-xs text-slate-700">Includes {card.scoredHighlights.join(", ")}</span>
      )}
    </button>
  );
}

function PickToggle({ m, pick }) {
  const key = pick.keyFor(m.canonicalName);
  const on = pick.pickedKeys.has(key);
  const disabled = !on && pick.capReached;
  return (
    <button type="button" onClick={() => pick.toggle(key)} disabled={disabled} aria-pressed={on}
      data-testid={`family-member-pick-${m.subjectId}`}
      className={`inline-flex min-h-[32px] shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : disabled ? "cursor-not-allowed border-stone-200 text-stone-400" : "border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F0FF]"}`}>
      {on ? <><Check className="h-3 w-3" aria-hidden="true" /> In my plan</> : "+ Add to plan"}
    </button>
  );
}

function MemberRow({ m, guide, pick, onRead, famSlug }) {
  return (
    <li className="flex flex-col gap-2 border-b border-slate-100 py-2 last:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`family-member-${m.subjectId}`}>
      <span className="flex min-w-0 flex-col">
        <span className="text-sm text-slate-900">{m.canonicalName}</span>
        {m.scored
          ? <span className="text-xs text-slate-700" data-testid={`family-member-score-${m.subjectId}`}>Fast Match score {m.displayScore}</span>
          : <span className="text-xs text-slate-500" data-testid={`family-member-unscored-${m.subjectId}`}>Exploratory · not scored yet</span>}
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {pick && <PickToggle m={m} pick={pick} />}
        {guide
          ? <Link to={guideHref(guide.slug, famSlug)} onClick={() => onRead(famSlug)} className="whitespace-nowrap font-medium text-[#7C3AED] hover:underline" data-testid={`family-member-guide-${m.subjectId}`}>Read subject guide</Link>
          : <span className="whitespace-nowrap text-slate-400" data-testid={`family-member-noguide-${m.subjectId}`}>No guide yet</span>}
      </span>
    </li>
  );
}

function FamilyMembers({ fam, collapsible, pick, onRead }) {
  const { bySubjectId } = usePublishedGuides();
  const list = <ul className="mt-2">{fam.members.map((m) => <MemberRow key={m.subjectId} m={m} guide={bySubjectId.get(m.subjectId)} pick={pick} onRead={onRead} famSlug={fam.slug} />)}</ul>;
  if (collapsible) {
    return (
      <details id={`ff-family-${fam.slug}`} className="scroll-mt-56 rounded-lg border border-slate-200 bg-white px-4 py-2" data-testid={`family-explored-${fam.slug}`}>
        <summary className="cursor-pointer text-sm font-semibold text-slate-900">{fam.family} <span className="font-normal text-slate-500">({fam.memberCount})</span></summary>
        {list}
      </details>
    );
  }
  return (
    <div id={`ff-family-${fam.slug}`} className="scroll-mt-56 rounded-xl border border-slate-200 bg-white p-4" data-testid={`family-explored-${fam.slug}`}>
      <h3 className="text-lg text-slate-900">{fam.family}</h3>
      {list}
    </div>
  );
}

function NoGuidesNote({ explored }) {
  const { loaded, bySubjectId } = usePublishedGuides();
  if (!loaded || explored.some((f) => f.members.some((m) => bySubjectId.has(m.subjectId)))) return null;
  return <p className="text-xs text-slate-500" data-testid="family-preview-no-guides">No subject guides are published for these subjects yet.</p>;
}

function ExploredView({ view, pick, onRead }) {
  const mode = view.choice?.mode;
  if (!mode) return null;
  if (mode === "unsure") {
    return <p className="mt-4 text-sm text-slate-600" data-testid="family-preview-unsure-note">No problem. Your results above stay as they are, so take your time.</p>;
  }
  return (
    <div className="mt-5 space-y-3" data-testid="family-preview-explored">
      {mode === "none" && <p className="text-sm text-slate-600" data-testid="family-preview-none-note">Here are all the other subject families. Open any of them to see their subjects.</p>}
      <p className="text-xs text-slate-500" data-testid="family-preview-exploratory-note">Exploratory subjects aren't scored in this beta yet. That says nothing about how well they fit you.</p>
      <NoGuidesNote explored={view.explored} />
      {view.explored.map((f) => <FamilyMembers key={f.slug} fam={f} collapsible={mode === "none"} pick={pick} onRead={onRead} />)}
    </div>
  );
}

function ChoiceControls({ view, picked, setPicked, busy, onSave }) {
  const toggle = (slug) => setPicked((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : p.length < 2 ? [...p, slug] : p));
  return (
    <>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="family-preview-cards">
        {view.cards.map((c) => (
          <FamilyCard key={c.slug} card={c} chosen={picked.includes(c.slug)}
            disabled={!picked.includes(c.slug) && picked.length >= 2} onToggle={() => toggle(c.slug)} />
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" disabled={busy || picked.length === 0} onClick={() => onSave("families", picked)}
          data-testid="family-preview-show-btn"
          className="rounded-full bg-[#7C3AED] px-4 py-2 text-sm font-medium text-white transition-opacity disabled:opacity-40">
          Show {picked.length === 2 ? "both families" : "this family"}
        </button>
        <button type="button" disabled={busy} onClick={() => onSave("unsure", [])} data-testid="family-preview-unsure-btn"
          className={`rounded-full border px-4 py-2 text-sm transition-colors ${view.choice?.mode === "unsure" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-800 hover:border-slate-900"}`}>
          I'm not sure yet
        </button>
        <button type="button" disabled={busy} onClick={() => onSave("none", [])} data-testid="family-preview-none-btn"
          className={`rounded-full border px-4 py-2 text-sm transition-colors ${view.choice?.mode === "none" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-800 hover:border-slate-900"}`}>
          None of these
        </button>
      </div>
    </>
  );
}

function Notices({ view, editedFrom }) {
  const review = view.savedPlanReview;
  return (
    <>
      {editedFrom && !view.choice && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="family-preview-edited-note">
          These are new results from your edited answers, so any family choice from your earlier attempt doesn't carry over. Choose again below.
        </p>
      )}
      {view.staleChoiceDiscarded && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="family-preview-stale-note">
          Your answers changed, so your earlier family choice no longer applies. Choose again below.
        </p>
      )}
      {review?.notInThisResult?.length > 0 && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="family-preview-plan-review">
          Your saved interests include {review.notInThisResult.join(", ")}, which {review.notInThisResult.length > 1 ? "aren't" : "isn't"} part of this result. Your saved plan hasn't been changed. Explore that family again to keep it, or remove it in the review box below.
        </p>
      )}
    </>
  );
}

export function FamilyPreviewPanel({ snapshot, session, onView, onSettled, editedFrom, pick, onRead, chosenNames = [] }) {
  const [view, setView] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const sid = snapshot?.sessionId;
  const token = session?.sessionId === sid ? session?.accessToken : null;
  const chosenKey = chosenNames.join("|");
  const chosenRef = useRef(chosenNames);
  chosenRef.current = chosenNames;

  useEffect(() => {
    let live = true;
    (async () => {
      const flag = await fetchFamilyPreviewFlag();
      if (!flag.ok || !flag.data?.enabled || !sid) { if (live) onSettled?.(); return; }
      const r = await fetchFamilyPreview(sid, token, chosenRef.current);
      if (!live) return;
      if (r.ok) { setView(r.data); onView?.(r.data); setPicked(r.data.choice?.mode === "families" ? r.data.choice.families : []); }
      else setError(r.error?.message || "Family preview unavailable");
      onSettled?.();
    })();
    return () => { live = false; };
  }, [sid, token, snapshot?.snapshotId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The student's chosen subjects rule the cards: refresh them as picks change (saved choice untouched).
  const firstChosen = useRef(true);
  useEffect(() => {
    if (firstChosen.current) { firstChosen.current = false; return undefined; }
    if (!view) return undefined;
    let live = true;
    const t = setTimeout(async () => {
      const r = await fetchFamilyPreview(sid, token, chosenRef.current);
      if (!live || !r.ok) return;
      setView(r.data); onView?.(r.data);
      setPicked((p) => p.filter((slug) => r.data.cards.some((c) => c.slug === slug)));
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [chosenKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return position from a guide: ?ffFamily=<slug> scrolls back to that family (opening it if collapsed).
  useEffect(() => {
    const fam = new URLSearchParams(window.location.search).get("ffFamily");
    if (!view || !fam) return;
    const el = document.getElementById(`ff-family-${fam}`) || document.querySelector('[data-testid="family-preview-panel"]');
    if (el?.tagName === "DETAILS") el.open = true;
    el?.scrollIntoView({ block: "start" });
  }, [view]);

  const onSave = async (mode, families) => {
    setBusy(true); setError(null);
    const r = await saveFamilyPreview(sid, token, { mode, families, chosen: chosenRef.current });
    setBusy(false);
    if (r.ok) { setView(r.data); onView?.(r.data); if (mode !== "families") setPicked([]); }
    else setError(r.error?.message || "Could not save your choice");
  };

  if (!view && !error) return null;
  return (
    <section className="mt-10 rounded-2xl border border-dashed border-[#7C3AED]/50 bg-white/70 p-5 sm:p-6 print:hidden" data-testid="family-preview-panel">
      <span className="inline-flex items-center gap-1 rounded-full bg-[#7C3AED]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#7C3AED]" data-testid="family-preview-badge">
        <FlaskConical className="h-3 w-3" aria-hidden="true" /> Preview
      </span>
      <h2 className="mt-2 text-xl font-editorial tracking-tight text-slate-900 sm:text-2xl">Explore subject families</h2>
      <p className="mt-1 text-sm text-slate-600">These families come from the subjects you've chosen, then your answers. Pick one or two to see every subject in them, and add any of them to your plan (up to 3 subjects). Your results above don't change.</p>
      {error && <p className="mt-3 text-sm text-rose-700" data-testid="family-preview-error">{error}</p>}
      {view && <ChoiceControls view={view} picked={picked} setPicked={setPicked} busy={busy} onSave={onSave} />}
      {view && <Notices view={view} editedFrom={editedFrom} />}
      {view && <ExploredView view={view} pick={pick} onRead={onRead} />}
    </section>
  );
}

